import {
  useAppDispatch,
  useAppSelector,
  setUserOrderPoint,
  setAnotherUserOrderPoint,
  setOrderPointLocationInfo,
  setDeliveryOrderPointCity,
  clearOrderPointLocationInfo,
  setDeliveryOrderPointAddress,
  setAnotherOrderPointLocationInfo,
  setAnotherDeliveryOrderPointCity,
  clearAnotherOrderPointLocationInfo,
  setAnotherDeliveryOrderPointAddress,
} from '@/app/store';
import { useCallback, useMemo } from 'react';
import {
  ClientOnlinePaymentDto,
  LocationPointDto,
  OrderPointAreaDto,
  OrderPointDto,
  PaymentTypeEnum,
} from '@/app/store/api/restApi';

export const useChangePersonalInfoOrderPoint = (isOrderAnotherPerson?: boolean) => {
  const dispatch = useAppDispatch();

  const orderPointMyself = useAppSelector((state) => state.order.orderPointDelivery);
  const orderPointAnother = useAppSelector((state) => state.orderAnotherPerson.orderPointDelivery);

  const orderPointDelivery = useMemo(() => {
    if (isOrderAnotherPerson) return orderPointAnother;
    return orderPointMyself;
  }, [isOrderAnotherPerson, orderPointAnother, orderPointMyself]);

  const onClearOrderPointLocationInfo = useCallback(() => {
    if (isOrderAnotherPerson) dispatch(clearAnotherOrderPointLocationInfo());
    else dispatch(clearOrderPointLocationInfo());
  }, [dispatch, isOrderAnotherPerson]);

  const onSetOrderPointLocationInfo = useCallback(
    ({
      locationId,
      deliveryPrice,
      minCalendarDays,
      maxCalendarDays,
      paymentTypes,
      onlinePayment,
    }: {
      locationId?: string;
      deliveryPrice?: number;
      minCalendarDays?: number;
      maxCalendarDays?: number;
      paymentTypes?: PaymentTypeEnum[];
      onlinePayment?: ClientOnlinePaymentDto;
    }) => {
      const payload = { locationId, deliveryPrice, minCalendarDays, maxCalendarDays, paymentTypes, onlinePayment };
      if (isOrderAnotherPerson) dispatch(setAnotherOrderPointLocationInfo(payload));
      else dispatch(setOrderPointLocationInfo(payload));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetDeliveryOrderPointCity = useCallback(
    ({
      city,
      fullCity,
      coordsCity,
      coordsArea,
    }: {
      city: string;
      fullCity: string;
      coordsCity?: LocationPointDto;
      coordsArea?: OrderPointAreaDto;
    }) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryOrderPointCity({ city, fullCity, coordsCity, coordsArea }));
      else dispatch(setDeliveryOrderPointCity({ city, fullCity, coordsCity, coordsArea }));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetDeliveryOrderPointAddress = useCallback(
    (orderPointAddress: string | undefined) => {
      if (isOrderAnotherPerson) dispatch(setAnotherDeliveryOrderPointAddress(orderPointAddress));
      else dispatch(setDeliveryOrderPointAddress(orderPointAddress));
    },
    [dispatch, isOrderAnotherPerson],
  );

  const onSetUserOrderPoint = useCallback(
    (orderPoint: OrderPointDto | undefined) => {
      if (isOrderAnotherPerson) dispatch(setAnotherUserOrderPoint(orderPoint));
      else dispatch(setUserOrderPoint(orderPoint));
    },
    [dispatch, isOrderAnotherPerson],
  );

  return {
    orderPointDelivery,
    onSetUserOrderPoint,
    onSetOrderPointLocationInfo,
    onSetDeliveryOrderPointCity,
    onClearOrderPointLocationInfo,
    onSetDeliveryOrderPointAddress,
  };
};
